// Asymmetric split-sleeper-berth tier. US Property (incl. California Property) is
// the sole asymmetric family, so the family evaluator lives here alongside the
// tier helpers it calls rather than in a separate family file.
import {
    HOURS,
    onDutyStatuses,
    offDutyStatuses,
    getSplitPeriodDuration,
    type IState,
    type ISbSplitPeriod,
    type ISplitInfoProperty,
    type ISplitPeriod,
    type IPropertySplitSubConfig,
    type IWorkday,
    type TWorkdayResetInfo
} from "../helpers";
import { isBigDayExemptionReset, isExemptionAppliedDuringWorkday } from "../exemptionHelpers";
import { startViolation, completeViolations, getConfigWorkdayMaximum } from "../hosCalculator";
import { appendCurrentSplitLog, beginWorkdayRecalc, completeSplit, isSplitCompleteBlockedByViolation } from "./shared";

// True when a split-period duration is an eligible "big" period: at least `min`, and — when a
// ceiling is configured — strictly under bigSplitMax. The minimum differs by caller (bigSplitMin,
// or smallSplitMin when only big periods freeze), but the ceiling check is identical everywhere,
// so it lives in one place to keep the eligibility rule from drifting across the three call sites.
const isDurationEligibleForBigSplit = (duration: number, min: number, bigSplitMax?: number): boolean =>
    duration >= min && (bigSplitMax === undefined || duration < bigSplitMax);

// 395.1 Sleeper berths
//
// 1. One off-duty period (whether in or out of the sleeper berth) is at least 2 hours long, and
// 2. The other involves at least 7 consecutive hours in the sleeper berth
// 3. Both periods added together must equal at least 10 hours
// 4. When paired, neither time period counts against the 14-hour driving window
// 5. 8-hour sleeper-berth period by itself can no longer be excluded from the 14-hour driving window
export const evaluateAsymmetricSplit = (state: IState) => {
    let splitInfo = state.workday.propertySplitSB;
    let splitConfig = state.propertySplitSubConfig;
    let workdayOffDutyMinimum = state.config.workday?.offDutyMinimum;
    if (!splitInfo || !splitConfig || workdayOffDutyMinimum === undefined || state.duration === 0) {
        return;
    }
    let isStatusOff = isEligibleOffDutyStatus(state.current.status);
    if (isStatusOff) {
        collectSplitPeriodsAsymmetric(state, splitInfo);
        if (isResetSplitPairingFixEnabled(state)) {
            refreshCarriedResetPairingExtension(state, splitInfo, splitConfig);
            preserveCompletedResetSbExtension(state, splitInfo, splitConfig);
        }
        freezeSplitPeriodAsymmetric(state, splitInfo, splitConfig);
    } else {
        forgetCarriedResetPairing(state.workday);
        splitInfo.inOffDuty = false;
        splitInfo.inSleeper = false;
        splitInfo.offDutyPeriods = splitInfo.offDutyPeriods.filter(o => splitConfig && getSplitPeriodDuration(o) >= splitConfig.smallSplitMin);
        splitInfo.sbPeriods = splitInfo.sbPeriods.filter(
            o =>
                splitConfig &&
                state.workday.offDutyMinimum &&
                isDurationEligibleForBigSplit(getSplitPeriodDuration(o), splitConfig.bigSplitMin, splitConfig.bigSplitMax)
        );
    }
    const { isSplitCompleted, workdayResetInfo, earnedSplitExclusion, carriedResetPairingSleeperStart } = isSplitCompleteAsymmetric(
        state,
        splitInfo,
        splitConfig.smallSplitMin,
        splitConfig.bigSplitMin,
        splitConfig.bigSplitMax
    );
    recordSplitLogsAsymmetric(state, splitInfo);
    if (isSplitCompleted) {
        if (earnedSplitExclusion !== undefined) {
            state.workday.earnedSplitExclusion = Math.max(state.workday.earnedSplitExclusion ?? 0, earnedSplitExclusion);
            state.workday.earnedSplitExclusionFromCarriedResetPairing = true;
            state.workday.carriedResetPairingSleeperStart = carriedResetPairingSleeperStart;
            state.workday.offDutySplitPeriodFromStandardWorkdayReset = undefined;
            recordCarriedPairingOnPreviousWorkday(state, workdayResetInfo);
        } else {
            completeSplit(state, workdayResetInfo);
        }
    }
};

// A pairing with the carried reset period does not start a new workday (the current one already began at the
// reset's end), so completeSplit()/startNewWorkday() never runs to stamp the pairing onto the previous workday.
// Replace that workday's plain offDutyMinimum reset with the completed pairing so the summary reports it the
// same way a normal completed split would.
const recordCarriedPairingOnPreviousWorkday = (state: IState, workdayResetInfo?: TWorkdayResetInfo) => {
    if (!workdayResetInfo || state.workdays[state.workdays.length - 1] !== state.workday) {
        return;
    }
    const previousWorkday = state.workdays.length > 1 ? state.workdays[state.workdays.length - 2] : undefined;
    if (previousWorkday && (!previousWorkday.resetInfo || previousWorkday.resetInfo.type === "offDutyMinimum")) {
        state.workday.resetInfoBeforeCarriedResetPairing = previousWorkday.resetInfo;
        state.workday.replacedPreviousWorkdayResetInfoForCarriedPairing = true;
        previousWorkday.resetInfo = workdayResetInfo;
    }
};

const restorePreviousWorkdayResetInfoBeforeCarriedPairing = (state: IState, sleeperStart: Date) => {
    if (!state.workday.replacedPreviousWorkdayResetInfoForCarriedPairing) {
        return;
    }
    const stampedWorkday = findWorkdayStampedWithCarriedPairing(state, sleeperStart);
    if (stampedWorkday) {
        stampedWorkday.resetInfo = state.workday.resetInfoBeforeCarriedResetPairing;
    }
    state.workday.resetInfoBeforeCarriedResetPairing = undefined;
    state.workday.replacedPreviousWorkdayResetInfoForCarriedPairing = undefined;
};

const forgetCarriedResetPairing = (workday: IWorkday) => {
    workday.resetInfoBeforeCarriedResetPairing = undefined;
    workday.replacedPreviousWorkdayResetInfoForCarriedPairing = undefined;
    workday.carriedResetPairingSleeperStart = undefined;
};

const getCarriedPairingSleeperStamp = (workday: IWorkday | undefined, sleeperStart: Date): ISbSplitPeriod | undefined => {
    const resetInfo = workday?.resetInfo;
    if (resetInfo?.type !== "asymmetricSplitSleeperBerth") {
        return undefined;
    }
    const laterLeg = resetInfo.periods[1];
    return laterLeg?.type === "sleeperBerth" && laterLeg.fromDate.getTime() === sleeperStart.getTime() ? laterLeg : undefined;
};

// The stamped workday is not always the one before the current workday: the rest holding the paired sleeper
// can itself reset the workday while the sleeper is still running, and a not-yet-started workday is popped and
// recreated on every off-duty log. The stamp's sleeper start identifies it regardless of how many workdays follow.
const findWorkdayStampedWithCarriedPairing = (state: IState, sleeperStart: Date): IWorkday | undefined => {
    for (let index = state.workdays.length - 1; index >= 0; index--) {
        const workday = state.workdays[index];
        if (workday !== state.workday && getCarriedPairingSleeperStamp(workday, sleeperStart)) {
            return workday;
        }
    }
    return undefined;
};

// For asymmetric (US Property) split drivers, recalculate Workday violations for the current workday.
// eslint-disable-next-line complexity
export const recalculateWorkdayViolationsForCurrentWorkdayAsymmetric = (state: IState) => {
    const splitConfig = state.propertySplitSubConfig;
    const recalc = beginWorkdayRecalc(state, !!state.workday.propertySplitSB?.offDutyPeriods.length, !!splitConfig);
    if (!recalc || !splitConfig) {
        return;
    }
    const { workdayMaximum, splitLogs, workdayConfig } = recalc;
    // A carried reset pair has completed without replaying through completeSplit(), so the regular
    // failed-split reconstruction must start with the exclusion it already earned. Otherwise a
    // later normal reset would backdate a violation as though the completed pairing never existed.
    const earnedSplitExclusion = state.workday.earnedSplitExclusionFromCarriedResetPairing
        ? state.workday.earnedSplitExclusion ?? 0
        : 0;
    let workdayEnd = recalc.workdayEnd + earnedSplitExclusion;
    let extended = state.config.bigDayBonus && state.workday.workdayExtended ? (workdayMaximum + state.workday.workdayExtended) / HOURS : undefined;
    let longestOffDuty = earnedSplitExclusion;
    let consecutiveOffDuty = 0;
    let isSplitBlocked = state.intermediateSplitConfig.violationRecalculatesWorkday && isSplitCompleteBlockedByViolation(state);
    for (let i = 0; i < splitLogs.length - 1; i++) {
        let log = splitLogs[i];
        let logEndDate = splitLogs[i + 1].dateTime;
        if (!isSplitBlocked && offDutyStatuses.indexOf(log.status) !== -1) {
            if (state.customCodes.disableRemoveOrTrimViolationsAfter16hException) {
                log.status !== "D" && delete state.currentViolations["Workday"]?.lastDriveToDate;
            }
            // Trying to use splitSBDuringReset to match with longestOffDuty so it can be used to extend this workday
            consecutiveOffDuty += logEndDate.getTime() - log.dateTime.getTime();
            if (
                consecutiveOffDuty >= splitConfig.smallSplitMin && // Small part of the SB
                state.splitSBDuringReset && // Big part of the SB
                state.splitSBDuringReset + consecutiveOffDuty >= workdayConfig.offDutyMinimum
            ) {
                // Remove previous longestOffDuty Add new longestOffDuty's logDuration as extended time to workdayEnd
                if (longestOffDuty < consecutiveOffDuty) {
                    workdayEnd -= longestOffDuty;
                    longestOffDuty = consecutiveOffDuty;
                    workdayEnd += longestOffDuty;
                }
            }
        } else if (log.status === "D") {
            consecutiveOffDuty = 0;
            // If D log goes past workdayEnd, start a violation
            if (logEndDate.getTime() > workdayEnd) {
                let fromDate = new Date(Math.max(log.dateTime.getTime(), workdayEnd));
                startViolation(state, log.id, "Workday", fromDate, logEndDate, extended);
            }
        } else if (!state.customCodes.disableUnfreezeSBSplitNotCompleteFix && onDutyStatuses.indexOf(log.status) !== -1) {
            consecutiveOffDuty = 0;
        }
    }
    completeViolations(state, "Workday", state.currentDate);
};

export const collectSplitPeriodsAsymmetric = (state: IState, splitInfo: ISplitInfoProperty) => {
    let isStatusSB = state.current.status === "SB";
    if (isStatusSB) {
        if (splitInfo.sbPeriods.length === 0 || !splitInfo.inSleeper) {
            splitInfo.sbPeriods.push({ fromDate: new Date(state.currentDate.getTime()), toDate: new Date(state.nextDate.getTime()) });
        } else {
            splitInfo.sbPeriods[splitInfo.sbPeriods.length - 1].toDate = new Date(state.nextDate.getTime());
        }
    }
    if (splitInfo.offDutyPeriods.length === 0 || !splitInfo.inOffDuty) {
        splitInfo.offDutyPeriods.push({ fromDate: new Date(state.currentDate.getTime()), toDate: new Date(state.nextDate.getTime()) });
    } else {
        splitInfo.offDutyPeriods[splitInfo.offDutyPeriods.length - 1].toDate = new Date(state.nextDate.getTime());
    }
    splitInfo.inOffDuty = true;
    splitInfo.inSleeper = isStatusSB;
};

const isLongEnoughForSmallSplit = (offDutyPeriod: ISplitPeriod, smallSplitMinimum: number) =>
    getSplitPeriodDuration(offDutyPeriod) >= smallSplitMinimum;

const offDutyPeriodsLongEnoughForSmallSplit = (splitInfo: ISplitInfoProperty, smallSplitMinimum: number) =>
    splitInfo.offDutyPeriods.filter(offDutyPeriod => isLongEnoughForSmallSplit(offDutyPeriod, smallSplitMinimum));

const hasAlreadyPairedWithAnEarlierSmallSplit = (state: IState, sbPeriod: ISplitPeriod) =>
    (state.sleeperStartsPairedWithAnEarlierSmallSplit ?? []).indexOf(sbPeriod.fromDate.getTime()) !== -1;

const rememberPairedWithAnEarlierSmallSplit = (state: IState, sbPeriod: ISplitPeriod) => {
    const alreadyRemembered = state.sleeperStartsPairedWithAnEarlierSmallSplit ?? [];
    state.sleeperStartsPairedWithAnEarlierSmallSplit = alreadyRemembered.concat(sbPeriod.fromDate.getTime());
};

// The winning rest becomes the earlier leg, so the new workday starts at its end. Taking the rest
// that ends last therefore starts that workday as late as possible, which is the most availability.
const endingLastFirst = (splitPeriods: ISplitPeriod[]) => [...splitPeriods].sort((one, other) => other.toDate.getTime() - one.toDate.getTime());

const isPairingReuseFixEnabled = (state: IState) => !state.customCodes.disableUsaPropertySplitPairingReuseFix;
const isResetSplitPairingFixEnabled = (state: IState) =>
    !state.customCodes.disableUsaPropertyResetSplitPairingFix && !state.customCodes.enableUsaPreventWorkdayResetPairingForward;

const inPairingPriorityOrder = (state: IState, smallSplitCandidates: ISplitPeriod[]) =>
    isPairingReuseFixEnabled(state) ? endingLastFirst(smallSplitCandidates) : smallSplitCandidates;

const isEligibleOffDutyStatus = (status: string) => offDutyStatuses.indexOf(status) !== -1 && status !== "WT";

const canCompleteAsymmetricSplit = (state: IState) =>
    !state.intermediateSplitConfig.violationPreventsSplitCompletion || !isSplitCompleteBlockedByViolation(state);

const clearCarriedResetPairing = (state: IState, splitInfo: ISplitInfoProperty, splitConfig: IPropertySplitSubConfig, sleeperStart: Date) => {
    restorePreviousWorkdayResetInfoBeforeCarriedPairing(state, sleeperStart);
    state.workday.earnedSplitExclusion = undefined;
    state.workday.earnedSplitExclusionFromCarriedResetPairing = undefined;
    state.workday.carriedResetPairingSleeperStart = undefined;
    state.sleeperStartsPairedWithAnEarlierSmallSplit = state.sleeperStartsPairedWithAnEarlierSmallSplit?.filter(
        start => start !== sleeperStart.getTime()
    );
    // The frozen sleeper may no longer be an eligible split leg (e.g. it grew past bigSplitMax into a
    // normal reset). freezeSplitPeriodAsymmetric() only rewrites the window while an eligible period
    // exists, so recompute it here or the provisional carried-pair extension would linger.
    setWorkdayEndFromSplitPeriods(state, splitInfo, splitConfig);
};

const hasQualifyingOrdinaryPairingForCarriedSleeper = (
    state: IState,
    splitInfo: ISplitInfoProperty,
    splitConfig: IPropertySplitSubConfig,
    sleeperPeriod: ISplitPeriod
) =>
    inPairingPriorityOrder(state, offDutyPeriodsLongEnoughForSmallSplit(splitInfo, splitConfig.smallSplitMin)).some(
        offDutyPeriod =>
            offDutyPeriod.toDate < sleeperPeriod.fromDate &&
            getSplitPeriodDuration(offDutyPeriod) + getSplitPeriodDuration(sleeperPeriod) >= 10 * HOURS
    );

const refreshCarriedResetPairingExtension = (state: IState, splitInfo: ISplitInfoProperty, splitConfig: IPropertySplitSubConfig) => {
    const sleeperStart = state.workday.carriedResetPairingSleeperStart;
    if (!sleeperStart) {
        return;
    }

    const pairedSleeperPeriod = splitInfo.sbPeriods.find(period => period.fromDate.getTime() === sleeperStart.getTime());
    if (!pairedSleeperPeriod) {
        refreshCarriedPairingStampAfterWorkdayReset(state, splitConfig, sleeperStart);
        return;
    }

    const pairedSleeperDuration = getSplitPeriodDuration(pairedSleeperPeriod);
    if (!isDurationEligibleForBigSplit(pairedSleeperDuration, splitConfig.bigSplitMin, splitConfig.bigSplitMax)) {
        // The period has become a normal full reset rather than an eligible split leg. It must undo
        // the provisional carried-pair exclusion recorded at an earlier evaluation checkpoint.
        clearCarriedResetPairing(state, splitInfo, splitConfig, sleeperStart);
        return;
    }

    if (hasQualifyingOrdinaryPairingForCarriedSleeper(state, splitInfo, splitConfig, pairedSleeperPeriod)) {
        // A carried reset can be the first eligible pairing at an intermediate checkpoint, but a
        // later-growing sleeper may make a newer ordinary OFF leg win. Restore the old reset summary
        // and let isSplitCompleteAsymmetric select/replay that ordinary pairing below.
        clearCarriedResetPairing(state, splitInfo, splitConfig, sleeperStart);
        return;
    }

    state.workday.earnedSplitExclusion = Math.max(state.workday.earnedSplitExclusion ?? 0, pairedSleeperDuration);

    // A dummy SB log means the period might continue in a subsequent validation. Once the next real
    // status is not SB, this is the final duration and the reset candidate must stay consumed.
    if (state.next.status !== "SB") {
        forgetCarriedResetPairing(state.workday);
    }
};

// The rest holding the paired sleeper can reach the workday minimum while the sleeper is still running, so
// startNewWorkday recreates the split tracker mid-sleeper and the continuation lands in the new tracker under
// its own start. The new workday earns nothing from that pairing; only the stamped summary still has to follow
// the sleeper to its real end, or be undone when the sleeper grows into a normal reset (bigSplitMax).
const refreshCarriedPairingStampAfterWorkdayReset = (state: IState, splitConfig: IPropertySplitSubConfig, sleeperStart: Date) => {
    const stampedSleeper = getCarriedPairingSleeperStamp(findWorkdayStampedWithCarriedPairing(state, sleeperStart), sleeperStart);
    if (!stampedSleeper || state.current.status !== "SB" || stampedSleeper.toDate.getTime() !== state.currentDate.getTime()) {
        forgetCarriedResetPairing(state.workday);
        return;
    }
    if (!isDurationEligibleForBigSplit(state.nextDate.getTime() - sleeperStart.getTime(), splitConfig.bigSplitMin, splitConfig.bigSplitMax)) {
        restorePreviousWorkdayResetInfoBeforeCarriedPairing(state, sleeperStart);
        forgetCarriedResetPairing(state.workday);
        return;
    }
    stampedSleeper.toDate = new Date(state.nextDate.getTime());
    if (state.next.status !== "SB") {
        forgetCarriedResetPairing(state.workday);
    }
};

const preserveCompletedResetSbExtension = (state: IState, splitInfo: ISplitInfoProperty, splitConfig: IPropertySplitSubConfig) => {
    // Same off-duty predicate as evaluateAsymmetricSplit and the reset recalculation loop, so PC/PC_Exempted/SB
    // short legs earn the same exclusion as OFF.
    if (
        !canCompleteAsymmetricSplit(state) ||
        !isEligibleOffDutyStatus(state.current.status) ||
        !state.splitSBDuringReset ||
        !state.workday.offDutyMinimum
    ) {
        return;
    }

    const currentOffDutyPeriod = splitInfo.offDutyPeriods[splitInfo.offDutyPeriods.length - 1];
    if (!currentOffDutyPeriod || currentOffDutyPeriod.toDate.getTime() !== state.nextDate.getTime()) {
        return;
    }

    const offDutyDuration = getSplitPeriodDuration(currentOffDutyPeriod);
    if (offDutyDuration < splitConfig.smallSplitMin || state.splitSBDuringReset + offDutyDuration < state.workday.offDutyMinimum) {
        return;
    }

    state.workday.earnedSplitExclusion = Math.max(state.workday.earnedSplitExclusion ?? 0, offDutyDuration);
};

export const isSplitCompleteAsymmetric = (
    state: IState,
    splitInfo: ISplitInfoProperty,
    smallSplitMinimum: number,
    bigSplitMinimum: number,
    bigSplitMaximum?: number
): {
    isSplitCompleted: boolean;
    workdayResetInfo?: TWorkdayResetInfo;
    earnedSplitExclusion?: number;
    carriedResetPairingSleeperStart?: Date;
} => {
    // Do not complete split when an enabled policy blocks completion for an ongoing violation.
    if (!canCompleteAsymmetricSplit(state)) {
        return { isSplitCompleted: false };
    }
    const smallSplitCandidates = offDutyPeriodsLongEnoughForSmallSplit(splitInfo, smallSplitMinimum);
    const carriedOffDutyPeriod = isResetSplitPairingFixEnabled(state) ? state.workday.offDutySplitPeriodFromStandardWorkdayReset : undefined;
    if (carriedOffDutyPeriod) {
        smallSplitCandidates.push(carriedOffDutyPeriod);
    }
    const candidatesInPriorityOrder = inPairingPriorityOrder(state, smallSplitCandidates);
    for (let candidateIndex = 0; candidateIndex < candidatesInPriorityOrder.length; candidateIndex++) {
        const offDutyPeriod = candidatesInPriorityOrder[candidateIndex];
        for (let sbPeriod of splitInfo.sbPeriods.filter(o =>
            isDurationEligibleForBigSplit(getSplitPeriodDuration(o), bigSplitMinimum, bigSplitMaximum)
        )) {
            // Need to have a combined 10 Hour to complete the split
            if (getSplitPeriodDuration(offDutyPeriod) + getSplitPeriodDuration(sbPeriod) >= 10 * HOURS) {
                if (offDutyPeriod.toDate < sbPeriod.fromDate) {
                    const isCarriedOffDutyPeriod =
                        carriedOffDutyPeriod &&
                        offDutyPeriod.fromDate.getTime() === carriedOffDutyPeriod.fromDate.getTime() &&
                        offDutyPeriod.toDate.getTime() === carriedOffDutyPeriod.toDate.getTime();
                    // A redundant SB log partitions one continuous sleeper period. If a higher-priority
                    // ordinary OFF leg would qualify after that same sleeper continues, wait for the next
                    // segment instead of consuming the carried reset candidate at the artificial boundary.
                    const hasHigherPriorityOrdinaryCandidateThatMayStillQualify =
                        isCarriedOffDutyPeriod &&
                        sbPeriod.toDate.getTime() === state.nextDate.getTime() &&
                        state.current.status === "SB" &&
                        state.next.status === "SB" &&
                        !state.next.dummyLog &&
                        candidatesInPriorityOrder.slice(0, candidateIndex).some(candidate =>
                            candidate.toDate < sbPeriod.fromDate &&
                            getSplitPeriodDuration(candidate) + getSplitPeriodDuration(sbPeriod) < 10 * HOURS
                        );
                    if (hasHigherPriorityOrdinaryCandidateThatMayStillQualify) {
                        continue;
                    }
                    if (isPairingReuseFixEnabled(state)) {
                        if (hasAlreadyPairedWithAnEarlierSmallSplit(state, sbPeriod)) {
                            continue;
                        }
                        rememberPairedWithAnEarlierSmallSplit(state, sbPeriod);
                    }

                    // The tracker wipe below prepares for the completeSplit() replay. A pairing with the
                    // carried reset period does not replay (the workday already starts at the reset's end),
                    // so the sleeper must stay in the tracker: freezeSplitPeriodAsymmetric() reads the
                    // tracker, and dropping the sleeper would let a later short OFF shrink the window.
                    if (!isCarriedOffDutyPeriod) {
                        state.workday.splitLogs = state.workday.splitLogs && state.workday.splitLogs.filter(o => o.dateTime >= offDutyPeriod.toDate);
                        splitInfo.offDutyPeriods = [];
                        splitInfo.sbPeriods = [sbPeriod];
                    }

                    // SB period completed the split

                    const offDutyDuration = getSplitPeriodDuration(offDutyPeriod);
                    const timeWhenSBMeetsMinimum = new Date(sbPeriod.fromDate.getTime() + bigSplitMinimum);
                    const timeWhenCombinedReaches10h = new Date(sbPeriod.fromDate.getTime() + (10 * HOURS - offDutyDuration));
                    const eligibleForResetDate =
                        timeWhenSBMeetsMinimum > timeWhenCombinedReaches10h ? timeWhenSBMeetsMinimum : timeWhenCombinedReaches10h;
                    const workdayResetInfo: TWorkdayResetInfo = {
                        type: "asymmetricSplitSleeperBerth",
                        periods: [
                            {
                                type: "offDuty",
                                fromDate: offDutyPeriod.fromDate,
                                toDate: offDutyPeriod.toDate
                            },
                            {
                                type: "sleeperBerth",
                                fromDate: sbPeriod.fromDate,
                                toDate: sbPeriod.toDate
                            }
                        ],
                        eligibleForResetDate
                    };
                    return {
                        isSplitCompleted: true,
                        workdayResetInfo,
                        earnedSplitExclusion: isCarriedOffDutyPeriod ? getSplitPeriodDuration(sbPeriod) : undefined,
                        carriedResetPairingSleeperStart: isCarriedOffDutyPeriod ? new Date(sbPeriod.fromDate) : undefined
                    };
                }
                if (offDutyPeriod.fromDate > sbPeriod.toDate) {
                    state.workday.splitLogs = state.workday.splitLogs && state.workday.splitLogs.filter(o => o.dateTime >= sbPeriod.toDate);
                    splitInfo.sbPeriods = [];
                    splitInfo.offDutyPeriods = [offDutyPeriod];

                    const sbDuration = getSplitPeriodDuration(sbPeriod);
                    const timeWhenOFFMeetsMinimum = new Date(offDutyPeriod.fromDate.getTime() + smallSplitMinimum);
                    const timeWhenCombinedReaches10h = new Date(offDutyPeriod.fromDate.getTime() + (10 * HOURS - sbDuration));
                    const eligibleForResetDate =
                        timeWhenOFFMeetsMinimum > timeWhenCombinedReaches10h ? timeWhenOFFMeetsMinimum : timeWhenCombinedReaches10h;
                    const workdayResetInfo: TWorkdayResetInfo = {
                        type: "asymmetricSplitSleeperBerth",
                        periods: [
                            {
                                type: "sleeperBerth",
                                fromDate: sbPeriod.fromDate,
                                toDate: sbPeriod.toDate
                            },
                            {
                                type: "offDuty",
                                fromDate: offDutyPeriod.fromDate,
                                toDate: offDutyPeriod.toDate
                            }
                        ],
                        eligibleForResetDate
                    };
                    return { isSplitCompleted: true, workdayResetInfo };
                }
            }
        }
    }
    return { isSplitCompleted: false };
};

export const getLargestSplitPeriodDuration = (splitInfo: ISplitInfoProperty, splitConfig: IPropertySplitSubConfig) => {
    let freezeMin = splitConfig.onlyFreezeBigPeriods ? splitConfig.bigSplitMin : splitConfig.smallSplitMin;
    const eligibleSplitPeriods = (splitConfig.onlyFreezeBigPeriods ? splitInfo.sbPeriods : splitInfo.offDutyPeriods)
        .map(period => getSplitPeriodDuration(period))
        .filter(duration => isDurationEligibleForBigSplit(duration, freezeMin, splitConfig.bigSplitMax));
    return eligibleSplitPeriods.length ? Math.max.apply(Math, eligibleSplitPeriods) : 0;
};

// The largest eligible split period does not count against the workday driving limit
export const freezeSplitPeriodAsymmetric = (state: IState, splitInfo: ISplitInfoProperty, splitConfig: IPropertySplitSubConfig) => {
    // If there is valid 16H exemption to be applied in the current workday or already applied, do not freeze the split period
    if (
        !state.workday.start ||
        !state.workday.end ||
        state.workday.is16HourExemptionApplied ||
        state.exemption16HLogs.filter(log => isExemptionAppliedDuringWorkday(state, log.dateTime) && isBigDayExemptionReset(state)).length > 0
    ) {
        return;
    }

    // Only an eligible split period rewrites the window. Rewriting on 0 would also drop extensions
    // that are not split periods, such as WT time added directly to workday.end.
    if (getLargestSplitPeriodDuration(splitInfo, splitConfig)) {
        setWorkdayEndFromSplitPeriods(state, splitInfo, splitConfig);
    }
};

const setWorkdayEndFromSplitPeriods = (state: IState, splitInfo: ISplitInfoProperty, splitConfig: IPropertySplitSubConfig) => {
    const workdayMaximum = getConfigWorkdayMaximum(state);
    if (!state.workday.start || !state.workday.end || !workdayMaximum) {
        return;
    }
    const largestPeriodDuration = getLargestSplitPeriodDuration(splitInfo, splitConfig);
    const workdayExtended = state.workday.workdayExtended ?? 0;
    state.workday.end.setTime(state.workday.start.getTime() + workdayMaximum + largestPeriodDuration + workdayExtended);
};

// splitting record logs since first split
export const recordSplitLogsAsymmetric = (state: IState, splitInfo: ISplitInfoProperty) => {
    if (state.workday) {
        if (splitInfo.offDutyPeriods.length > 0 || splitInfo.sbPeriods.length > 0) {
            appendCurrentSplitLog(state);
        } else {
            state.workday.splitLogs = [];
        }
    }
};
